/**
 * RS cart drawer — toast, open on add, bundle upsell, cart toggle sync.
 * Layout/behavior parity with landing-page/cart-drawer.js (Shopify cart API backend).
 */
(function () {
  const TOAST_DURATION_MS = 2400;

  function getDrawer() {
    return document.getElementById("cart-drawer") || document.querySelector("cart-drawer.rs-cart-drawer");
  }

  function getCartToggle() {
    return document.getElementById("cart-toggle");
  }

  function getToast() {
    return document.querySelector("cart-drawer.rs-cart-drawer .cart-drawer__toast");
  }

  function setToggleExpanded(expanded) {
    getCartToggle()?.setAttribute("aria-expanded", String(expanded));
  }

  function showToast() {
    const toast = getToast();
    if (!toast) return;

    toast.hidden = false;
    window.clearTimeout(showToast._timer);
    showToast._timer = window.setTimeout(() => {
      toast.hidden = true;
    }, TOAST_DURATION_MS);
  }

  function openDrawer() {
    const drawer = getDrawer();
    if (!drawer || typeof drawer.show !== "function") return;
    drawer.show();
  }

  function initCartToggle() {
    const drawer = getDrawer();
    if (!drawer) return;

    drawer.addEventListener("dialog:after-show", () => setToggleExpanded(true));
    drawer.addEventListener("dialog:after-hide", () => setToggleExpanded(false));
    setToggleExpanded(Boolean(drawer.open));
  }

  function getCartDrawerSectionIds() {
    const sections = [];
    document.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } })
    );

    if (!sections.length) {
      const drawer = getDrawer();
      const sectionEl = drawer?.closest("[id^='shopify-section-']");
      if (sectionEl) {
        sections.push(sectionEl.id.replace("shopify-section-", ""));
      }
    }

    return sections;
  }

  async function fetchCartDrawerSections(sectionIds) {
    const shopRoot = window.Shopify?.routes?.root || "/";
    const sections = {};

    await Promise.all(
      sectionIds.map(async (id) => {
        const response = await fetch(`${shopRoot}?section_id=${encodeURIComponent(id)}`);
        sections[id] = await response.text();
      })
    );

    return sections;
  }

  async function addVariantsToCart(variantIds) {
    const shopRoot = window.Shopify?.routes?.root || "/";
    const sectionIds = getCartDrawerSectionIds();
    const items = variantIds.map((id) => ({ id: Number(id), quantity: 1 }));
    const payload = { items };

    if (sectionIds.length) {
      payload.sections = sectionIds.join(",");
      payload.sections_url = `${window.location.pathname}${window.location.search}`;
    }

    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const responseJson = await response.json();
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));

      if (!response.ok) {
        throw new Error(responseJson?.description || responseJson?.message || "Add to cart failed");
      }

      const cartContent = await (await fetch(`${shopRoot}cart.js`)).json();
      let sections = responseJson.sections || {};
      const missingSectionIds = sectionIds.filter((id) => !sections[id]);

      if (missingSectionIds.length) {
        sections = { ...sections, ...(await fetchCartDrawerSections(missingSectionIds)) };
      }

      cartContent.sections = sections;

      document.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            onSuccessDo: "force_open_drawer",
            cart: cartContent,
          },
        })
      );
    } catch (error) {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      console.error("RS cart drawer: bundle add failed", error);
    }
  }

  function onVariantAdded() {
    showToast();
    openDrawer();
  }

  document.addEventListener("cart:change", (event) => {
    if (event.detail?.baseEvent !== "variant:add") return;
    onVariantAdded();
  });

  document.addEventListener("cart-drawer:refreshed", () => {
    const toast = getToast();
    if (toast && !toast.hidden) {
      showToast();
    }
  });

  document.addEventListener("click", (event) => {
    const bundleBtn = event.target.closest("[data-rs-cart-bundle]");
    if (!bundleBtn) return;

    event.preventDefault();
    const variantIds = (bundleBtn.dataset.variantIds || "")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);

    if (!variantIds.length) return;

    bundleBtn.setAttribute("aria-busy", "true");
    bundleBtn.disabled = true;

    addVariantsToCart(variantIds).finally(() => {
      bundleBtn.removeAttribute("aria-busy");
      bundleBtn.disabled = false;
    });
  });

  function init() {
    initCartToggle();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
